import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import type { SkillLevel, SkillNode } from "../types";
// import { skillsRoot } from '../data/skills';
import { flattenLeaves } from "../utils/flatten";
import { firstGoogleLink } from "../utils/google";
import useSkillsStore from "../state/store";
import DrillsDialog from "./DrillsDialog";

const th = {
  bgcolor: "#101a3a",
  color: "#cfe0ff",
  fontWeight: 700,
  cursor: "pointer",
  userSelect: "none",
} as const;
const td = { color: "#e5ecff" } as const;

// ---- helpers ---------------------------------------------------------------

function findById(n: SkillNode, id: string): SkillNode | null {
  if (!n) return null;
  if ((n as any).id === id) return n;
  for (const c of n.children ?? []) {
    const f = findById(c as any, id);
    if (f) return f;
  }
  return null;
}

type SortKey = "leaf" | "parent" | "level";
type SortDir = "asc" | "desc";
const flip = (d: SortDir): SortDir => (d === "asc" ? "desc" : "asc");

function parentFromPath(path: string): string {
  const parts = path.split(" › ");
  return parts.length >= 2 ? parts[parts.length - 2] : "—";
}

function cmpStr(a = "", b = "") {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}
function cmpNum(a: number, b: number) {
  return a === b ? 0 : a < b ? -1 : 1;
}

// ---- component -------------------------------------------------------------

export default function LevelTableDialog({
  isSmall,
  isLandscape,
  open,
  level,
  onClose,
  mode = "level",
  categoryId = null,
  titleOverride,
  idIndex,
  skillsRoot,
}: {
  isSmall: boolean,
  isLandscape: boolean;
  open: boolean;
  level: SkillLevel | null;
  onClose: () => void;
  skillsRoot: SkillNode;
  mode?: "level" | "category";
  categoryId?: string | null;
  idIndex?: Record<string, SkillNode>;
  titleOverride?: string;
}) {
  const openWindow = useSkillsStore((s) => s.openWindow);

  const [drillsOpen, setDrillsOpen] = useState(false);
  const [drillLeaf, setDrillLeaf] = useState<any>(null);
  const [drillHierarchy, setDrillHierarchy] = useState("");

  // NEW: sorting state
  const [sortKey, setSortKey] = useState<SortKey>("level");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Build base rows (your earlier logic, unchanged in spirit)
  const base = useMemo(() => {
    if (mode === "category" && categoryId) {
      //const cat = findById(skillsRoot as unknown as SkillNode, categoryId);
      const cat =
        idIndex?.[categoryId] ??
        findById(skillsRoot as unknown as SkillNode, categoryId);
      if (!cat) return { rows: [], title: "Skills" };
      const leaves = flattenLeaves(cat as unknown as SkillNode);
      // keep existing priority+path ordering as our “ingest order”
      const scored = leaves
        .map((r) => ({
          ...r,
          _p: (r.node as any).priority ?? Number.POSITIVE_INFINITY,
        }))
        .sort((a, b) =>
          a._p === b._p ? a.path.localeCompare(b.path) : a._p - b._p
        );
      return {
        rows: scored,
        title: titleOverride ?? `Skills for ${(cat as any).name ?? "Category"}`,
      };
    }

    if (!level) return { rows: [], title: "Skills" };
    const leaves = flattenLeaves(skillsRoot as unknown as SkillNode);
    const filtered = leaves.filter(({ node }) => {
      const leafLevel = (node as any).level as string | undefined;
      return !!leafLevel && leafLevel === level;
    });
    const scored = filtered
      .map((r) => ({
        ...r,
        _p: (r.node as any).priority ?? Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) =>
        a._p === b._p ? a.path.localeCompare(b.path) : a._p - b._p
      );
    return {
      rows: scored,
      title: titleOverride ?? `Skills for Level ${level}`,
    };
  }, [mode, level, categoryId, titleOverride]);

  // // Apply sort key/dir to base.rows
  // const rows = useMemo(() => {
  //   const out = [...base.rows];
  //   out.sort((A: any, B: any) => {
  //     let c = 0;
  //     if (sortKey === 'leaf') {
  //       c = cmpStr(A.name, B.name);
  //     } else if (sortKey === 'parent') {
  //       c = cmpStr(parentFromPath(A.path), parentFromPath(B.path));
  //     } else if (sortKey === 'level') {
  //       const a = parseFloat((A.node?.level ?? '0').toString());
  //       const b = parseFloat((B.node?.level ?? '0').toString());
  //       c = cmpNum(a, b);
  //     }
  //     return sortDir === 'asc' ? c : -c;
  //   });
  //   return out;
  // }, [base.rows, sortKey, sortDir]);

  // Apply sort key/dir to base.rows (robust to non-numeric level strings)
  const LEVEL_ORDER: Record<string, number> = {
    "3.5": 3.5,
    "4.0": 4.0,
    "4.5": 4.5,
    "5.0": 5.0,
    "5.5": 5.5,
    "6.0": 6.0,
    "6.0 Invisible Skills": 6.01, // nudge so it sorts just after plain 6.0
  };

  const rows = useMemo(() => {
    const out = [...base.rows];

    out.sort((A: any, B: any) => {
      let c = 0;

      if (sortKey === "leaf") {
        c = cmpStr(A.name, B.name);
      } else if (sortKey === "parent") {
        c = cmpStr(parentFromPath(A.path), parentFromPath(B.path));
      } else if (sortKey === "level") {
        const la = (A.node?.level ?? "") as string;
        const lb = (B.node?.level ?? "") as string;

        // use mapping first; fall back to parseFloat; then to string compare
        const na =
          LEVEL_ORDER[la] ?? Number.isFinite(parseFloat(la))
            ? parseFloat(la)
            : Number.POSITIVE_INFINITY;
        const nb =
          LEVEL_ORDER[lb] ?? Number.isFinite(parseFloat(lb))
            ? parseFloat(lb)
            : Number.POSITIVE_INFINITY;

        c = cmpNum(na, nb) || cmpStr(la, lb);
      }

      return sortDir === "asc" ? c : -c;
    });

    return out;
  }, [base.rows, sortKey, sortDir]);

  const title = base.title;

  const openDrills = (row: any) => {
    const parts = row.path.split(" › ");
    const parentToRoot = parts.slice(0, -1).reverse().join(" ← ");
    setDrillLeaf(row.node);
    setDrillHierarchy(parentToRoot);
    setDrillsOpen(true);
  };

  const clickHeader = (key: SortKey) => () => {
    if (sortKey === key) setSortDir(flip(sortDir));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // const SortChevron = ({ active }: { active: boolean }) =>
  //   active ? (
  //     sortDir === "asc" ? (
  //       <ArrowDropUpIcon sx={{ ml: 0.5 }} />
  //     ) : (
  //       <ArrowDropDownIcon sx={{ ml: 0.5 }} />
  //     )
  //   ) : null;

const SortChevron = ({ active }: { active: boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,        // reserve space so columns don't move
      ml: 0.5,
    }}
  >
    {sortDir === 'asc' ? (
      <ArrowDropUpIcon sx={{ fontSize:isSmall ? 14: 18, opacity: active ? 1 : 0 }} />
    ) : (
      <ArrowDropDownIcon sx={{ fontSize: isSmall ? 14: 18, opacity: active ? 1 : 0 }} />
    )}
  </Box>
);


  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        container={document.body}
        sx={{ zIndex: "2000000 !important" }}
        slotProps={{
  backdrop: {
    sx: {
      backgroundColor: "rgba(0,0,0,0.10)",
    },
  },
  paper: {
    sx: {
      overflow: "hidden",
    },
  },
}}
        // slotProps={{
        //   backdrop: { sx: { zIndex:200000}}, // (t) => t.zIndex.modal + 1 } },
        //   paper: {
        //     sx: {
        //       position: "relative",
        //       zIndex: (t:any) => t.zIndex.modal + 2,
        //       overflow: "hidden",
        //     },
        //   },
        // }}
      >
        <Box sx={{ bgcolor: "#0b1020", color: "#e5ecff", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ fontWeight: 700, fontSize: isSmall ? 14: 18 }}>{title}</Box>
            <IconButton onClick={onClose} sx={{ ml: "auto" }}>
              <CloseIcon sx={{ color: "#9fb3ff" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              border: "1px solid #1f2b57",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: "70vh",
              fontSize: isSmall ? 12: 16
            }}
          >
            <Table size="small" sx={{fontSize: isSmall ? 12: 16, "& th, & td":{fontSize: isSmall ? 12: 16 }}} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={th} onClick={clickHeader("leaf")}>
                    Leaf <SortChevron active={sortKey === "leaf"} />
                  </TableCell>
                  <TableCell sx={th} onClick={clickHeader("parent")}>
                    Hierarchy (parent ← grandparent ← …){" "}
                    <SortChevron active={sortKey === "parent"} />
                  </TableCell>
                  <TableCell sx={th} onClick={clickHeader("level")}>
                    Level <SortChevron active={sortKey === "level"} />
                  </TableCell>
                  <TableCell sx={{ ...th, cursor: "default" }}>
                    Drills
                  </TableCell>
                  <TableCell sx={{ ...th, cursor: "default" }}>
                    Find more drills
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell sx={td} colSpan={5}>
                      No skills at this exact level yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(({ id, name, path, node, parentId }) => {
                    const parts = path.split(" › ");
                    const parentToRoot =
                      parts.slice(0, -1).reverse().join(" ← ") || "—";
                    return (
                      <TableRow key={id} hover>
                        <TableCell sx={td}>{name}</TableCell>
                        <TableCell sx={td}>
                          {parentToRoot}
                          {parentId && (
                            <>
                              {" "}
                              {/* •{" "}
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ color: "#9fb3ff" }}
                                onClick={() => openWindow(parentId, id)}
                              >
                                Open parent tree
                              </Link> */}
                            </>
                          )}
                        </TableCell>
                        <TableCell sx={td}>
                          {(node as any).level ?? "—"}
                        </TableCell>
                        <TableCell sx={td}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              openDrills({ id, name, path, node, parentId })
                            }
                            sx={{ color: "#cfe0ff", borderColor: "#1f2b57" }}
                          >
                            OPEN DRILLS
                          </Button>
                        </TableCell>
                        <TableCell sx={td}>
                          <Link
                            href={firstGoogleLink(node)}
                            target="_blank"
                            underline="hover"
                          >
                            Find more drills
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Dialog>

      <DrillsDialog
            isSmall={isSmall}
isLandscape={isLandscape}
        open={drillsOpen}
        onClose={() => setDrillsOpen(false)}
        leaf={drillLeaf}
        reverseHierarchy={drillHierarchy}
      />
    </>
  );
}
